import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Card,
  DatePicker,
  Button,
  message,
  Space,
  Row,
  Col,
  Statistic,
  Typography,
  Select,
  Tag,
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  aulaDadaService,
  equipeService,
  turmaService,
  unidadeService,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeRole } from '../utils/rbacFrontend';
import dayjs from 'dayjs';

const { Title } = Typography;

const STATUS_OPTIONS = ['PENDENTE', 'VALIDADA', 'REJEITADA'];

const STATUS_COLOR = {
  PENDENTE:  'orange',
  VALIDADA:  'green',
  REJEITADA: 'red',
};

const TOTAIS_ZERO = {
  total_aulas:      0,
  total_horas:      0,
  total_validadas:  0,
  total_rejeitadas: 0,
  total_pendentes:  0,
};

const CSV_HEADERS_ALL = [
  { key: 'data_aula',       label: 'Data' },
  { key: 'nome_unidade',    label: 'Unidade' },
  { key: 'nome_modalidade', label: 'Modalidade' },
  { key: 'nome_turma',      label: 'Turma' },
  { key: 'nome_professor',  label: 'Professor' },
  { key: 'hora_inicio',     label: 'Início' },
  { key: 'hora_fim',        label: 'Fim' },
  { key: 'duracao_horas',   label: 'Duração (h)' },
  { key: 'status',          label: 'Status' },
  { key: 'observacoes',     label: 'Observação / Motivo' },
];

const buildCsvContent = (headers, rows) => {
  const escape = (value) => {
    const text = String(value ?? '');
    return `"${text.replace(/"/g, '""')}"`;
  };
  const headerLine = headers.map((h) => escape(h.label)).join(';');
  const rowLines   = rows.map((row) =>
    headers.map((h) => escape(row[h.key] ?? '')).join(';')
  );
  return `﻿${[headerLine, ...rowLines].join('\n')}`;
};

const toArray = (responseData) => {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.items)) return responseData.items;
  return [];
};

const RelatorioMensal = () => {
  const { user } = useAuth();
  const isProfessor = normalizeRole(user?.role) === 'PROFESSOR';

  const [loadingData,    setLoadingData]    = useState(false);
  const [loadingFiltros, setLoadingFiltros] = useState(false);
  const [items,          setItems]          = useState([]);
  const [totais,         setTotais]         = useState(TOTAIS_ZERO);
  const [hasQueried,     setHasQueried]     = useState(false);

  const [mesSelecionado, setMesSelecionado] = useState(dayjs());
  const [unidades,       setUnidades]       = useState([]);
  const [equipes,        setEquipes]        = useState([]);
  const [turmas,         setTurmas]         = useState([]);

  const [filtros, setFiltros] = useState({
    id_unidade: null,
    id_equipe:  null,
    id_turma:   null,
    status:     null,
  });

  const setFiltro = useCallback((key, value) => {
    setFiltros((prev) => ({ ...prev, [key]: value ?? null }));
  }, []);

  // ── Carregamento das listas de filtros ──────────────────────────
  const loadFiltros = useCallback(async () => {
    setLoadingFiltros(true);
    try {
      const calls = [
        unidadeService.list(),
        turmaService.list(),
        isProfessor ? Promise.resolve(null) : equipeService.list(),
      ];
      const [resUnidades, resTurmas, resEquipes] = await Promise.all(calls);
      setUnidades(toArray(resUnidades?.data));
      setTurmas(toArray(resTurmas?.data));
      setEquipes(toArray(resEquipes?.data));
    } catch {
      message.error('Erro ao carregar filtros.');
    } finally {
      setLoadingFiltros(false);
    }
  }, [isProfessor]);

  useEffect(() => {
    loadFiltros();
  }, [loadFiltros]);

  // ── Busca do relatório ──────────────────────────────────────────
  const fetchRelatorio = useCallback(async () => {
    if (!mesSelecionado) {
      message.warning('Selecione o mês/competência.');
      return;
    }
    setLoadingData(true);
    try {
      const params = {
        mes:   mesSelecionado.month() + 1,
        ano:   mesSelecionado.year(),
        limit: 1000,
      };
      if (!isProfessor) {
        if (filtros.id_unidade) params.id_unidade = filtros.id_unidade;
        if (filtros.id_equipe)  params.id_equipe  = filtros.id_equipe;
      }
      if (filtros.id_turma) params.id_turma = filtros.id_turma;
      if (filtros.status)   params.status   = filtros.status;

      const response = await aulaDadaService.relatorioAulasDadas(params);
      const payload  = response?.data ?? {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotais(payload.totais ?? TOTAIS_ZERO);
      setHasQueried(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : 'Erro ao gerar relatório de aulas dadas.';
      message.error(msg);
    } finally {
      setLoadingData(false);
    }
  }, [mesSelecionado, filtros, isProfessor]);

  // ── Exportação CSV ──────────────────────────────────────────────
  const exportarCSV = useCallback(() => {
    if (items.length === 0) {
      message.warning('Nenhum dado para exportar.');
      return;
    }
    const headers = isProfessor
      ? CSV_HEADERS_ALL.filter((h) => h.key !== 'nome_professor')
      : CSV_HEADERS_ALL;

    const csvContent = buildCsvContent(headers, items);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `relatorio-aulas-${mesSelecionado.format('MM-YYYY')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success(`CSV exportado com ${items.length} registro(s).`);
  }, [items, isProfessor, mesSelecionado]);

  // ── Colunas da tabela ───────────────────────────────────────────
  const columns = useMemo(() => {
    const all = [
      {
        title:            'Data',
        dataIndex:        'data_aula',
        key:              'data_aula',
        width:            110,
        defaultSortOrder: 'ascend',
        sorter:           (a, b) => (a.data_aula > b.data_aula ? 1 : -1),
        render:           (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
      },
      {
        title:     'Unidade',
        dataIndex: 'nome_unidade',
        key:       'nome_unidade',
        width:     150,
        ellipsis:  true,
      },
      {
        title:     'Modalidade',
        dataIndex: 'nome_modalidade',
        key:       'nome_modalidade',
        width:     120,
        ellipsis:  true,
      },
      {
        title:     'Turma',
        dataIndex: 'nome_turma',
        key:       'nome_turma',
        width:     160,
        ellipsis:  true,
      },
      {
        title:     'Professor',
        dataIndex: 'nome_professor',
        key:       'nome_professor',
        width:     160,
        ellipsis:  true,
      },
      {
        title:     'Início',
        dataIndex: 'hora_inicio',
        key:       'hora_inicio',
        width:     75,
        align:     'center',
        render:    (val) => (val ? String(val).slice(0, 5) : '-'),
      },
      {
        title:     'Fim',
        dataIndex: 'hora_fim',
        key:       'hora_fim',
        width:     75,
        align:     'center',
        render:    (val) => (val ? String(val).slice(0, 5) : '-'),
      },
      {
        title:     'Duração',
        dataIndex: 'duracao_horas',
        key:       'duracao_horas',
        width:     90,
        align:     'right',
        render:    (val) => (val != null ? `${Number(val).toFixed(2)}h` : '-'),
      },
      {
        title:     'Status',
        dataIndex: 'status',
        key:       'status',
        width:     110,
        align:     'center',
        render:    (val) =>
          val ? (
            <Tag color={STATUS_COLOR[val] ?? 'default'}>{val}</Tag>
          ) : '-',
      },
      {
        title:     'Observação / Motivo',
        dataIndex: 'observacoes',
        key:       'observacoes',
        ellipsis:  true,
        render:    (val) => val ?? '',
      },
    ];

    return isProfessor
      ? all.filter((c) => c.key !== 'nome_professor')
      : all;
  }, [isProfessor]);

  // ── Layout de colunas do filtro (somam 24 em md) ────────────────
  // Não-PROFESSOR: 4+4+5+4+4+3 = 24
  // PROFESSOR:     4+0+0+8+6+6 = 24 (colunas condicionais não existem)
  const mdTurma   = isProfessor ? 8 : 4;
  const mdStatus  = isProfessor ? 6 : 4;
  const mdBotoes  = isProfessor ? 6 : 3;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Relatório de Aulas Dadas</Title>

      {/* ── Filtros ─────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]} align="bottom">

          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4 }}>Competência</div>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={mesSelecionado}
              onChange={(date) => setMesSelecionado(date)}
              placeholder="Selecione o mês"
              style={{ width: '100%' }}
            />
          </Col>

          {!isProfessor && (
            <Col xs={24} sm={12} md={4}>
              <div style={{ marginBottom: 4 }}>Unidade</div>
              <Select
                allowClear
                placeholder="Todas"
                value={filtros.id_unidade}
                onChange={(v) => setFiltro('id_unidade', v)}
                loading={loadingFiltros}
                style={{ width: '100%' }}
                options={unidades.map((u) => ({
                  label: u.nome_fantasia || u.nome,
                  value: u.id,
                }))}
              />
            </Col>
          )}

          {!isProfessor && (
            <Col xs={24} sm={12} md={5}>
              <div style={{ marginBottom: 4 }}>Professor</div>
              <Select
                allowClear
                placeholder="Todos"
                value={filtros.id_equipe}
                onChange={(v) => setFiltro('id_equipe', v)}
                loading={loadingFiltros}
                style={{ width: '100%' }}
                options={equipes.map((e) => ({
                  label: e.nome,
                  value: e.id,
                }))}
              />
            </Col>
          )}

          <Col xs={24} sm={12} md={mdTurma}>
            <div style={{ marginBottom: 4 }}>Turma</div>
            <Select
              allowClear
              placeholder="Todas"
              value={filtros.id_turma}
              onChange={(v) => setFiltro('id_turma', v)}
              loading={loadingFiltros}
              style={{ width: '100%' }}
              options={turmas.map((t) => ({
                label: t.nome,
                value: t.id,
              }))}
            />
          </Col>

          <Col xs={24} sm={12} md={mdStatus}>
            <div style={{ marginBottom: 4 }}>Status</div>
            <Select
              allowClear
              placeholder="Todos"
              value={filtros.status}
              onChange={(v) => setFiltro('status', v)}
              style={{ width: '100%' }}
              options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
            />
          </Col>

          <Col xs={24} sm={24} md={mdBotoes}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={fetchRelatorio}
                loading={loadingData}
              >
                Gerar
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportarCSV}
                disabled={items.length === 0}
              >
                CSV
              </Button>
            </Space>
          </Col>

        </Row>
      </Card>

      {/* ── Cards de totais ─────────────────────────────────────── */}
      {hasQueried && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={5}>
            <Card>
              <Statistic
                title="Total de Aulas"
                value={totais.total_aulas}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Card>
              <Statistic
                title="Total de Horas"
                value={Number(totais.total_horas).toFixed(2)}
                suffix="h"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={8} sm={8} md={4}>
            <Card>
              <Statistic
                title="Validadas"
                value={totais.total_validadas}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={8} sm={8} md={5}>
            <Card>
              <Statistic
                title="Rejeitadas"
                value={totais.total_rejeitadas}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
          <Col xs={8} sm={8} md={5}>
            <Card>
              <Statistic
                title="Pendentes"
                value={totais.total_pendentes}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* ── Tabela ──────────────────────────────────────────────── */}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loadingData}
        bordered
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize:        50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal:       (total) => `${total} aula(s)`,
        }}
        locale={{
          emptyText: hasQueried
            ? 'Nenhuma aula encontrada para os filtros selecionados.'
            : 'Selecione os filtros e clique em Gerar para carregar o relatório.',
        }}
      />
    </div>
  );
};

export default RelatorioMensal;
